import Logger from '~/utils/logger/logger';
import SocketEventType from '~/constants/socket-event-type';

/** How long a view stays hidden before its socket is suspended. */
export const WS_VISIBILITY_GRACE_MS = 60_000;

/** Options for {@link WebSocketCore}. */
export interface WebSocketCoreOptions {
  /** WebSocket endpoint the token is appended to. */
  endpoint: string;
  heartbeatMs: number;
  /** Resolved on every connect, so a rotated auth state is always used. */
  fetchToken: () => Promise<string | undefined>;
  /** Gates watchdog reconnects, e.g. on an authenticated user. */
  canConnect: () => boolean;
  /** Receives every non-heartbeat message and `SocketEventType.Reconnection`. */
  emit: (type: string, data?: unknown) => void;
  /** Connections already made this session; when > 0 the first open emits `Reconnection`. */
  initialConnectionsCount?: number;
}

/**
 * Single-socket websocket connection with heartbeat watchdog, backoff reconnects and
 * `suspend()`/`resume()`. A suspended or disconnected instance never reconnects on its own.
 */
export class WebSocketCore {
  private readonly options: WebSocketCoreOptions;
  private socket: WebSocket | undefined;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatUuid: string | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private connectionFailures = 0;
  private connectionsMade: number;
  private suspended = false;
  private stopped = false;
  private generation = 0;
  private pendingGeneration: number | null = null;

  constructor(options: WebSocketCoreOptions) {
    this.options = options;
    this.connectionsMade = options.initialConnectionsCount ?? 0;
  }

  get isSuspended() {
    return this.suspended;
  }

  /** Connections made this session, including `initialConnectionsCount`. */
  get connectionsCount() {
    return this.connectionsMade;
  }

  connect = async () => {
    const generation = this.generation;
    if (this.stopped || this.suspended || this.pendingGeneration === generation) {
      return;
    }
    this.pendingGeneration = generation;
    let token: string | undefined;
    try {
      token = await this.options.fetchToken();
    } catch {
      Logger.log('Failed to fetch WS token');
    } finally {
      if (this.pendingGeneration === generation) {
        this.pendingGeneration = null;
      }
    }
    if (generation !== this.generation) {
      return;
    }
    if (!token) {
      Logger.log('Empty WS token');
      this.scheduleReconnect();
      return;
    }
    this.detachSocket('replaced');
    try {
      this.socket = new WebSocket(this.options.endpoint + token);
    } catch (error) {
      Logger.error('Failed to create WebSocket', error);
      this.scheduleReconnect();
      return;
    }
    this.socket.addEventListener('open', this.onOpen);
    this.socket.addEventListener('message', this.onMessage);
    this.socket.addEventListener('close', this.onClose);
    this.socket.addEventListener('error', this.onError);
  };

  suspend = () => {
    this.suspended = true;
    this.halt('suspended');
  };

  resume = async () => {
    if (!this.suspended || this.stopped) {
      return;
    }
    this.suspended = false;
    await this.connectOrRetry();
  };

  disconnect = () => {
    this.stopped = true;
    this.halt();
  };

  private halt(reason?: string) {
    this.generation += 1;
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.detachSocket(reason);
  }

  private detachSocket(reason?: string) {
    if (!this.socket) {
      return;
    }
    const socket = this.socket;
    this.socket = undefined;
    socket.removeEventListener('open', this.onOpen);
    socket.removeEventListener('message', this.onMessage);
    socket.removeEventListener('close', this.onClose);
    socket.removeEventListener('error', this.onError);
    Logger.log('WebSocketService disconnecting' + (reason ? ': ' + reason : ''));
    try {
      socket.close();
    } catch (error) {
      Logger.error(error);
    }
  }

  private readonly onOpen = () => {
    Logger.log('SocketService connected');
    if (this.connectionsMade > 0) {
      this.options.emit(SocketEventType.Reconnection);
    }
    this.connectionsMade += 1;
    this.connectionFailures = 0;
    this.startHeartbeat();
  };

  private readonly onMessage = (msg: MessageEvent) => {
    const data = JSON.parse(msg.data);
    if (data.type === 'HEARTBEAT') {
      if (data.id === this.heartbeatUuid) {
        this.heartbeatUuid = null;
        Logger.log('HB response received successfully');
      } else {
        Logger.error('HB response UUID mismatch');
      }
      return;
    }
    Logger.log('onMessage', data.type, data);
    this.options.emit(data.type, data);
  };

  private readonly onClose = (event: CloseEvent) => {
    Logger.log('WebSocketService disconnected: ' + event?.code);
    this.stopHeartbeat();
    this.scheduleReconnect();
  };

  private async connectOrRetry() {
    if (this.options.canConnect()) {
      await this.connect();
    } else {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.suspended || this.stopped) {
      return;
    }
    this.connectionFailures += 1;
    const reconnectionTimeoutSec = this.connectionFailures > 5 ? 30 : this.connectionFailures * 5;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      void this.connectOrRetry();
    }, 1000 * reconnectionTimeoutSec);
  }

  private readonly onError = (...args: unknown[]) => {
    Logger.error('WebSocketService error:', ...args);
  };

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.heartbeatUuid = null;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(this.runHeartbeat, this.options.heartbeatMs);
    this.runHeartbeat();
  }

  private readonly runHeartbeat = () => {
    if (this.heartbeatUuid) {
      this.stopHeartbeat();
      this.detachSocket('HB failure');
      void this.connectOrRetry();
      return;
    }
    this.heartbeatUuid = window.crypto.randomUUID();
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'HEARTBEAT', id: this.heartbeatUuid }));
      Logger.log('HB sent');
    } else {
      Logger.error('WebSocket is not open. Cannot send heartbeat.');
    }
  };
}
